/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { createContext, useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const SearchContext = createContext();

export function SearchProvider({ children }) {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Perform search when query changes (with debounce)
  useEffect(() => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      performGlobalSearch(query.trim());
    }, 500); // Debounce 500ms

    return () => clearTimeout(timeoutId);
  }, [query]);

  const performGlobalSearch = async (searchQuery) => {
    setIsSearching(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
      const token = localStorage.getItem("authToken");


      const results = [];

      // Search in Stores - Exact word matching
      // Search in Stores - Exact word matching
      try {
        const storesRes = await fetch(`${baseUrl}/GetAllTenantFullDetails`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });

        if (storesRes.ok) {
          let stores = [];

          const text = await storesRes.text();
          try {
            stores = JSON.parse(text);
          } catch (e) {
            console.error("❌ Failed to parse stores JSON:", e);
            stores = [];
          }

          if (!Array.isArray(stores)) stores = [stores];

          const searchWords = searchQuery.toLowerCase().split(/\s+/).filter(w => w.length > 0);

          const filtered = stores.filter(store => {
            const searchableText = [
              store.TenantName,
              store.EngTenantName,
              store.CompanyName,
              store.Email,
              store.Phone,
              store.Domain,
              store.Descripsion,
              store.EngDescripsion
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();

            return searchWords.every(word => searchableText.includes(word));
          });

          filtered.forEach(store => {
            results.push({
              type: "store",
              title: store.TenantName || store.CompanyName,
              description: store.Email || store.Phone,
              link: "/admin/stores",
              data: store
            });
          });
        }
      } catch (e) {
        console.error("Error searching stores:", e);
      }


      // Search in Plans - Exact word matching
      try {
        const plansRes = await fetch(`${baseUrl}/Plans/GetPlan?PlanId=-1`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
        });
        if (plansRes.ok) {
          const plans = await plansRes.json();
          if (Array.isArray(plans)) {
            const searchWords = searchQuery.toLowerCase().split(/\s+/).filter(w => w.length > 0);
            const filtered = plans.filter(plan => {
              const searchableText = [
                plan.planType,
                plan.engPlanType,
                plan.descripsion,
                plan.engDescripsion,
                plan.note1,
                plan.note2,
                plan.note3,
                plan.note4,
                plan.note5,
                plan.note6,
                plan.note7,
                plan.note8
              ].filter(Boolean).join(' ').toLowerCase();

              return searchWords.every(word => searchableText.includes(word));
            });
            filtered.forEach(plan => {
              results.push({
                type: 'plan',
                title: plan.planType,
                description: plan.descripsion || '',
                link: '/admin/plans',
                data: plan
              });
            });
          }
        }
      } catch (e) {
        console.error("Error searching plans:", e);
      }

      // Search in Payments - Exact word matching
      try {
        const tenantId = localStorage.getItem("tenantId");
        if (tenantId) {
          const paymentsRes = await fetch(`${baseUrl}/GetTenantPayment?tenantId=${tenantId}`, {
            headers: {
              "Authorization": token ? `Bearer ${token}` : "",
            },
          });
          if (paymentsRes.ok) {
            const payments = await paymentsRes.json();
            if (Array.isArray(payments)) {
              const searchWords = searchQuery.toLowerCase().split(/\s+/).filter(w => w.length > 0);
              const filtered = payments.filter(payment => {
                const searchableText = [
                  payment.TransactionId?.toString(),
                  payment.TransactionAmount?.toString(),
                  payment.PaymentMethodType,
                  payment.TransactionStatus,
                  payment.TransactionCurrency,
                  payment.TransactionDate
                ].filter(Boolean).join(' ').toLowerCase();

                return searchWords.every(word => searchableText.includes(word));
              });
              filtered.forEach(payment => {
                results.push({
                  type: 'payment',
                  title: `Payment #${payment.TransactionId}`,
                  description: `${payment.TransactionAmount} ${payment.TransactionCurrency || 'IQD'}`,
                  link: '/admin/payments',
                  data: payment
                });
              });
            }
          }
        }
      } catch (e) {
        console.error("Error searching payments:", e);
      }

      // Search in Employees/Users - Exact word matching
      try {
        const tenantId = localStorage.getItem("tenantId");
        if (tenantId) {
          const usersRes = await fetch(`${baseUrl}/Users/GetUsers?tenantId=${tenantId}`, {
            headers: {
              "Authorization": token ? `Bearer ${token}` : "",
            },
          });
          if (usersRes.ok) {
            const users = await usersRes.json();
            if (Array.isArray(users)) {
              const searchWords = searchQuery.toLowerCase().split(/\s+/).filter(w => w.length > 0);
              const filtered = users.filter(user => {
                const searchableText = [
                  user.userName,
                  user.engUserName,
                  user.email,
                  user.userTypeID?.toString(),
                  user.userFunctionID?.toString()
                ].filter(Boolean).join(' ').toLowerCase();

                return searchWords.every(word => searchableText.includes(word));
              });
              filtered.forEach(user => {
                results.push({
                  type: 'employee',
                  title: user.userName,
                  description: user.email || '',
                  link: '/admin/employees',
                  data: user
                });
              });
            }
          }
        }
      } catch (e) {
        console.error("Error searching employees:", e);
      }

      setSearchResults(results);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (searchQuery) => {
    setQuery(searchQuery);
    // Navigation will happen on Enter key press or when user clicks search icon
  };

  return (
    <SearchContext.Provider value={{
      query,
      setQuery,
      searchResults,
      isSearching,
      handleSearch,
      performGlobalSearch
    }}>
      {children}
    </SearchContext.Provider>
  );
}

export const useSearch = () => useContext(SearchContext);
